import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import React from "react";
import UseContext from "../../../../../hooks/useContext";
import ModalBody from "../../../components/body";
import {
  Cutlery,
  EmptyStateMissionTimeSheetPeriod,
  Entry,
  Exit,
  Pause,
  Play,
} from "../../../../../assets/icons";
import { durationInDecimals, floatToTime } from "../../../../../utils/Time";
import { dateShort } from "../../../../../utils/Date";

const FIELD_LABELS = {
  name: "Nom",
  userIds: "Utilisateurs autorisés",
  authorizedUsers: "Utilisateurs autorisés",
  idMission: "Référence mission",
  workerLabel: "Employé",
  sectorLabel: "Secteur",
  qualificationLabel: "Poste",
  contractType: "Contrat",
  interimAgency: "Agence d'intérim",
  dates: "Période",
  totalHours: "Total heures travaillées",
};

const renderActivityDetailPanel = (detail) => {
  if (detail == null) {
    return (
      <div className="flex h-full min-h-[120px] items-center justify-center text-gray-400">
        —
      </div>
    );
  }
  if (typeof detail !== "object" || Array.isArray(detail)) {
    return (
      <div className="break-words text-sm text-gray-900">{String(detail)}</div>
    );
  }

  const entries = Object.entries(detail).filter(([key, val]) => {
    if (key === "markups") return false;
    if (key === "userIds" && Array.isArray(detail.authorizedUsers))
      return false;
    if (key === "workerId" && detail.workerLabel) return false;
    if (key === "sectorId" && detail.sectorLabel) return false;
    if (key === "qualificationId" && detail.qualificationLabel) return false;
    return val !== undefined && val !== null;
  });

  if (entries.length === 0) {
    return (
      <div className="flex h-full min-h-[120px] items-center justify-center text-gray-400">
        —
      </div>
    );
  }

  const formatValue = (key, val) => {
    if (key === "authorizedUsers" && Array.isArray(val)) {
      if (val.length === 0) return "Aucun";
      return val
        .map((u) =>
          `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.id || "—",
        )
        .join(", ");
    }
    if (key === "workerLabel" && val && typeof val === "object") {
      const full = `${val.firstname || ""} ${val.lastname || ""}`.trim();
      return full || val.id || "—";
    }
    if (
      (key === "sectorLabel" || key === "qualificationLabel") &&
      val &&
      typeof val === "object"
    ) {
      return val.name || val.id || "—";
    }
    if (key === "userIds" && Array.isArray(val)) {
      return val.length === 0
        ? "Aucun"
        : `${val.length} : ${val.join(", ")}`;
    }
    if (key === "interimAgency" && val && typeof val === "object") {
      const parts = [val.name, val.reference].filter(Boolean);
      return parts.length > 0 ? parts.join(" · ") : JSON.stringify(val);
    }
    if (
      key === "dates" &&
      val &&
      typeof val === "object" &&
      val.start &&
      val.end
    ) {
      return `${dateShort(val.start)} > ${dateShort(val.end)}`;
    }
    if (typeof val === "object") {
      return JSON.stringify(val, null, 2);
    }
    return String(val);
  };

  return (
    <dl className="flex flex-col gap-3 text-sm">
      {entries.map(([key, val]) => (
        <div key={key}>
          <dt className="text-xs font-semibold text-gray-700">
            {FIELD_LABELS[key] ?? key}
          </dt>
          <dd className="mt-0.5 whitespace-pre-wrap break-words text-gray-900">
            {formatValue(key, val)}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const ModalLogTimeSheet = () => {
  const { toggle } = useModal();
  const { modalData, timeCut } = UseContext();

  const beforeMarkups = modalData?.details?.before?.markups ?? [];
  const afterMarkups = modalData?.details?.after?.markups ?? [];
  const beforeTotalHours = modalData?.details?.before?.totalHours ?? 0;
  const afterTotalHours = modalData?.details?.after?.totalHours ?? 0;

  const hasMarkupTimeline =
    beforeMarkups.length > 0 || afterMarkups.length > 0;

  const missionPhrase = modalData?.mission
    ? ` pour la ${modalData.mission.toLowerCase()}`
    : "";

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Consulter les modifications
        <div className={"text-sm text-gray-500"}>
          Consultez les modifications effectuées par{" "}
          {modalData?.employee ?? "—"}
          {missionPhrase} le {modalData?.date ?? "—"}.
        </div>
      </Modalheader>
      <ModalBody>
        {hasMarkupTimeline ? (
        <div className={"w-full h-full flex"}>
          <div
            className={
              "flex flex-col border rounded-lg rounded-r-none w-full h-[300px]"
            }
          >
            <div className={"flex flex-col h-full overflow-y-auto"}>
              {beforeMarkups.length > 0 ? (
                <div className={"flex flex-col"}>
                  {beforeMarkups.map((markup, index) => {
                    const filteredMarkup = Object.entries(markup).filter(
                      ([key, value]) => key === "start" || key === "end",
                    );

                    return filteredMarkup.map(([key, value], i) => {
                      if (value) {
                        const getType = () => {
                          if (key === "start") {
                            if (index === 0) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-blue-500 p-2"}
                                  >
                                    <Entry wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Entrée",
                              };
                            } else if (
                              beforeMarkups[index - 1]?.end
                                ? durationInDecimals(
                                    beforeMarkups[index - 1].end,
                                    value,
                                  ) <= timeCut
                                : false
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Cutlery wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Fin de pause",
                              };
                            } else {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Play wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Fin de coupure",
                              };
                            }
                          } else if (key === "end") {
                            if (
                              index === beforeMarkups.length - 1
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-blue-500 p-2"}
                                  >
                                    <Exit wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Sortie",
                              };
                            } else if (
                              beforeMarkups[index + 1]?.start
                                ? durationInDecimals(
                                    value,
                                    beforeMarkups[index + 1].start,
                                  ) <= timeCut
                                : false
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Cutlery wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Début de pause",
                              };
                            } else {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Pause wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Début de coupure",
                              };
                            }
                          }
                          return { icon: "", name: "" };
                        };

                        const type = getType();

                        return (
                          <div
                            key={i}
                            className={`flex justify-between items-center p-4`}
                          >
                            <div className={"flex gap-6 items-center"}>
                              {type && <div>{type.icon}</div>}
                              {type && <div>{type.name}</div>}
                            </div>
                            <div
                              className={"flex justify-end gap-6 items-center"}
                            >
                              {value && <div>{value}</div>}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    });
                  })}
                </div>
              ) : (
                <div className={"m-auto"}>
                  <div
                    className={
                      "text-sm flex flex-col gap-1 items-center text-center max-w-[250px]"
                    }
                  >
                    <EmptyStateMissionTimeSheetPeriod
                      wh={64}
                      color={"#374151"}
                    />
                    <div className={"font-bold"}>Aucune donnée</div>
                    <div className={"text-gray-500"}>
                      Aucun pointage n’a été trouvé pour cette journée.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={
                "border-t min-h-[65px] py-4 px-4 flex flex-col justify-center text-sm"
              }
            >
              <div className={"font-black"}>Avant</div>
              {beforeTotalHours !== 0 && beforeMarkups.length > 0 && (
                  <div className={"text-gray-500"}>
                    Total heures travaillées :{" "}
                    {floatToTime(beforeTotalHours)}
                  </div>
                )}
            </div>
          </div>
          <div
            className={
              "flex flex-col border rounded-lg rounded-l-none w-full h-[300px]"
            }
          >
            <div className={"flex flex-col h-full overflow-y-auto"}>
              {afterMarkups.length > 0 ? (
                <div className={"flex flex-col"}>
                  {afterMarkups.map((markup, index) => {
                    const filteredMarkup = Object.entries(markup).filter(
                      ([key, value]) => key === "start" || key === "end",
                    );

                    return filteredMarkup.map(([key, value], i) => {
                      if (value) {
                        const getType = () => {
                          if (key === "start") {
                            if (index === 0) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-blue-500 p-2"}
                                  >
                                    <Entry wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Entrée",
                              };
                            } else if (
                              afterMarkups[index - 1]?.end
                                ? durationInDecimals(
                                    afterMarkups[index - 1].end,
                                    value,
                                  ) <= timeCut
                                : false
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Cutlery wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Fin de pause",
                              };
                            } else {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Play wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Fin de coupure",
                              };
                            }
                          } else if (key === "end") {
                            if (
                              index === afterMarkups.length - 1
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-blue-500 p-2"}
                                  >
                                    <Exit wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Sortie",
                              };
                            } else if (
                              afterMarkups[index + 1]?.start
                                ? durationInDecimals(
                                    value,
                                    afterMarkups[index + 1].start,
                                  ) <= timeCut
                                : false
                            ) {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Cutlery wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Début de pause",
                              };
                            } else {
                              return {
                                icon: (
                                  <div
                                    className={"rounded-full bg-yellow-500 p-2"}
                                  >
                                    <Pause wh={20} color={"#FFFFFF"} />
                                  </div>
                                ),
                                name: "Début de coupure",
                              };
                            }
                          }
                          return { icon: "", name: "" };
                        };

                        const type = getType();

                        return (
                          <div
                            key={i}
                            className={`flex justify-between items-center p-4`}
                          >
                            <div className={"flex gap-6 items-center"}>
                              {type && <div>{type.icon}</div>}
                              {type && <div>{type.name}</div>}
                            </div>
                            <div
                              className={"flex justify-end gap-6 items-center"}
                            >
                              {value && <div>{value}</div>}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    });
                  })}
                </div>
              ) : (
                <div className={"m-auto"}>
                  <div
                    className={
                      "text-sm flex flex-col gap-1 items-center text-center max-w-[250px]"
                    }
                  >
                    <EmptyStateMissionTimeSheetPeriod
                      wh={64}
                      color={"#374151"}
                    />
                    <div className={"font-bold"}>Aucune donnée</div>
                    <div className={"text-gray-500"}>
                      Aucun pointage n’a été trouvé pour cette journée.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={
                "border-t min-h-[65px] py-4 px-4 flex flex-col justify-center text-sm"
              }
            >
              <div className={"font-black"}>Après modification</div>
              {afterTotalHours !== 0 && afterMarkups.length > 0 && (
                  <div className={"text-gray-500"}>
                    Total heures travaillées :{" "}
                    {floatToTime(afterTotalHours)}
                  </div>
                )}
            </div>
          </div>
        </div>
        ) : (
        <div className={"flex w-full gap-0"}>
          <div
            className={
              "flex h-[300px] w-full flex-col rounded-lg rounded-r-none border"
            }
          >
            <div
              className={"flex h-full flex-col overflow-y-auto p-4 text-sm"}
            >
              {renderActivityDetailPanel(modalData?.details?.before)}
            </div>
            <div
              className={
                "flex min-h-[65px] flex-col justify-center border-t px-4 py-4 text-sm"
              }
            >
              <div className={"font-black"}>Avant</div>
            </div>
          </div>
          <div
            className={
              "flex h-[300px] w-full flex-col rounded-lg rounded-l-none border"
            }
          >
            <div
              className={"flex h-full flex-col overflow-y-auto p-4 text-sm"}
            >
              {renderActivityDetailPanel(modalData?.details?.after)}
            </div>
            <div
              className={
                "flex min-h-[65px] flex-col justify-center border-t px-4 py-4 text-sm"
              }
            >
              <div className={"font-black"}>Après modification</div>
            </div>
          </div>
        </div>
        )}
      </ModalBody>
    </>
  );
};

export default ModalLogTimeSheet;
